import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { PageHeader, PageSkeleton, EmptyState, KpiCard, StatusBadge, Avatar } from "../../components/fields";
import { ReplyThread } from "../../components/ReplyThread";
import { MessageSquareText, CheckCircle2, Inbox, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function CoachingInbox() {
  const [comments, setComments] = useState(null);
  const [students, setStudents] = useState([]);
  const navigate = useNavigate();

  const load = () => { api.get("/coaching-comments").then((r) => { setComments(r.data); window.dispatchEvent(new Event("coaching-read")); }); };

  useEffect(() => {
    load();
    api.get("/students").then((r) => setStudents(r.data));
  }, []);

  const acknowledge = async (id) => {
    try { await api.put(`/coaching-comments/${id}/acknowledge`); toast.success("Acknowledged"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!comments) return <PageSkeleton />;
  const studentName = (id) => {
    const s = students.find((x) => x.id === id);
    return s ? `${s.first_name} ${s.last_name}` : null;
  };

  const sevenDaysAgo = new Date(Date.now() - 7 * 864e5).toISOString();
  const recent = comments.filter((c) => c.created_at >= sevenDaysAgo).length;
  const acked = comments.filter((c) => c.acknowledged_at).length;
  const unacked = comments.length - acked;

  const statusChip = (c) => {
    if (c.acknowledged_at) return <StatusBadge tone="success" testId={`ack-badge-${c.id}`}><CheckCircle2 size={11} /> Acknowledged</StatusBadge>;
    if ((c.replies || []).length > 0) return <StatusBadge tone="info">Reviewed</StatusBadge>;
    return <StatusBadge tone="warning">New</StatusBadge>;
  };

  return (
    <div className="space-y-5 stagger">
      <PageHeader title="Coaching" subtitle="Feedback, guidance, and support from your TAG supervisor." />

      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <KpiCard testId="coaching-kpi-unread" label="Awaiting Acknowledgment" value={unacked} icon={Inbox} tone={unacked > 0 ? "amber" : "green"} sub={unacked > 0 ? "Review below" : "All acknowledged"} />
        <KpiCard testId="coaching-kpi-recent" label="Recent Feedback" value={recent} icon={Sparkles} tone="blue" sub="Last 7 days" />
        <KpiCard testId="coaching-kpi-acked" label="Acknowledged" value={acked} icon={CheckCircle2} tone="green" sub="All time" />
      </div>

      {comments.length === 0 && (
        <div className="bg-white border border-[#E5E9EE] rounded-2xl">
          <EmptyState icon={MessageSquareText} title="You're all caught up" hint="No coaching feedback needs your attention. When your supervisor reviews your documentation and leaves feedback, it appears here." />
        </div>
      )}

      <div className="space-y-3">
        {comments.map((c) => (
          <div key={c.id} data-testid={`coaching-item-${c.id}`} className="bg-white border border-[#E5E9EE] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
            <div className="flex items-center gap-3 flex-wrap">
              <Avatar name={c.supervisor_name} tone="navy" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#132238]">{c.supervisor_name}</p>
                <p className="text-xs text-[#667085]">{c.date}{studentName(c.student_id) ? ` · re: ${studentName(c.student_id)}` : ""}{c.entry_type !== "general" ? ` · on a ${c.entry_type.replace("_", " ")}` : ""}</p>
              </div>
              {statusChip(c)}
              {studentName(c.student_id) && (
                <button data-testid={`coaching-view-student-${c.id}`} onClick={() => navigate(`/students/${c.student_id}/dashboard`)}
                  className="text-xs font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-3 py-1.5 rounded-full transition-colors duration-150">View student</button>
              )}
            </div>
            <p className="text-sm text-[#132238] leading-relaxed mt-3.5 bg-[#F7F9FA] border border-[#E5E9EE] rounded-xl px-4 py-3">{c.comment}</p>
            {!c.acknowledged_at && (
              <div className="mt-3.5">
                <button data-testid={`ack-btn-${c.id}`} onClick={() => acknowledge(c.id)}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-[#087F5B] text-white hover:bg-[#066548] px-3.5 py-2 rounded-[10px] transition-colors duration-150 active:scale-95">
                  <CheckCircle2 size={13} /> Acknowledge
                </button>
              </div>
            )}
            <ReplyThread comment={c} onChanged={load} />
          </div>
        ))}
      </div>
    </div>
  );
}
